import { useEffect, useRef } from "react";
import { formatRelative } from "./format";
import type { useWorkvivoDirectoryRefresh } from "./hooks";
import type { DirectorySync } from "./types";

// The snapshot is refreshed on a schedule every 30 minutes; only auto-refresh on open if runs were missed.
const STALE_AFTER_MS = 2 * 60 * 60 * 1000;

function isStale(sync: DirectorySync | undefined): boolean {
  if (!sync) return false;
  if (sync.status === "never" || !sync.finished_at) return true;
  const finished = new Date(sync.finished_at).getTime();
  if (Number.isNaN(finished)) return true;
  return Date.now() - finished > STALE_AFTER_MS;
}

/**
 * Compact status bar shown at the top of the directory pages: reports when the cached
 * Workvivo snapshot was last refreshed and lets the user trigger another refresh.
 * Auto-starts a refresh (once per mount) when the snapshot has never loaded or is stale.
 */
export default function WorkvivoSyncBar({
  sync,
  refresh
}: {
  sync: DirectorySync | undefined;
  refresh: ReturnType<typeof useWorkvivoDirectoryRefresh>;
}) {
  const autoStartedRef = useRef(false);

  useEffect(() => {
    if (autoStartedRef.current || !sync) return;
    if (isStale(sync)) {
      autoStartedRef.current = true;
      void refresh.start();
    }
  }, [sync, refresh.start]);

  const isRunning = refresh.isRunning;
  const hasError = Boolean(refresh.error);
  const fetched = refresh.progress?.fetched ?? sync?.fetched ?? 0;
  const totalResults = refresh.progress?.total_results ?? sync?.total_results ?? null;

  const statusText =
    sync && sync.status !== "never" && sync.finished_at
      ? `Workvivo data as of ${formatRelative(sync.finished_at)}`
      : "Workvivo data has not been loaded yet";

  let buttonLabel = "Refresh from Workvivo";
  if (isRunning) buttonLabel = "Refreshing…";
  else if (hasError) buttonLabel = "Try again";

  return (
    <div className="directory-sync-bar">
      <div className="directory-sync-bar-text">
        <span className={hasError ? "directory-sync-bar-error" : undefined} role={hasError ? "alert" : undefined}>
          {hasError ? refresh.error : statusText}
        </span>
        {isRunning ? (
          <div className="directory-progress" role="status" aria-live="polite">
            <span className="directory-progress-label">
              Fetched {fetched}
              {totalResults ? ` of ${totalResults}` : ""} users…
            </span>
            <span className="directory-progress-track" aria-hidden>
              <span className="directory-progress-bar" />
            </span>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="directory-action-link directory-sync-bar-btn"
        onClick={() => void refresh.start()}
        disabled={isRunning}
      >
        <span aria-hidden>↻</span> {buttonLabel}
      </button>
    </div>
  );
}
